import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useResizeObserver } from '@react-hookz/web';
import {
  Play as PlayIcon,
  Pause as PauseIcon,
  SpeakerHigh as VolumeIcon,
  SpeakerSlash as MuteIcon,
  FastForward as FastForwardIcon,
  CaretDown as CaretDownIcon,
} from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { cn } from 'src/lib/utils';

// ---------------------------------------------------------------------------
// Shared video-scale + action-overlay helpers (used by both this player and
// the screenshot gallery in AgentSessionViewer).
// ---------------------------------------------------------------------------

export interface VideoScale {
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
}

export function computeContainedScale(
  clientW: number,
  clientH: number,
  naturalW: number,
  naturalH: number,
): VideoScale {
  const elementAspect = clientW / clientH;
  const contentAspect = naturalW / naturalH;
  let renderedW: number;
  let renderedH: number;
  if (contentAspect > elementAspect) {
    renderedW = clientW;
    renderedH = clientW / contentAspect;
  } else {
    renderedH = clientH;
    renderedW = clientH * contentAspect;
  }
  return {
    scaleX: renderedW / naturalW,
    scaleY: renderedH / naturalH,
    offsetX: (clientW - renderedW) / 2,
    offsetY: (clientH - renderedH) / 2,
  };
}

const CLICK_ACTIONS = new Set([
  'left_click',
  'right_click',
  'double_click',
  'triple_click',
  'middle_click',
  'left_click_drag',
]);

export function isClickAction(action: string | undefined): boolean {
  return Boolean(action && CLICK_ACTIONS.has(action));
}

export function formatActionLabel(
  action: string | undefined,
  text?: string,
): string {
  if (!action) return '';
  const name = action
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  if (!text) return name;
  if (action === 'type') return `${name} (${text.length} chars)`;
  return `${name}: ${text}`;
}

// ClickRipple — animated ripple at a coordinate within a scaled video/image.
export function ClickRipple({
  x,
  y,
  scale,
}: {
  x: number;
  y: number;
  scale: VideoScale;
}) {
  const style = {
    left: scale.offsetX + x * scale.scaleX,
    top: scale.offsetY + y * scale.scaleY,
    transform: 'translate(-50%, -50%)',
  };
  return (
    <div className="pointer-events-none absolute" style={style}>
      <div className="relative flex items-center justify-center">
        <span className="absolute h-6 w-6 animate-ping rounded-full bg-red-500/40" />
        <span className="relative h-3 w-3 rounded-full bg-red-500" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Player types
// ---------------------------------------------------------------------------

/** A single action anchored to a point on the recording timeline. */
export interface VideoActionMarker {
  id: string;
  /** Absolute wall-clock time of the action (epoch ms). */
  timestampMs: number;
  /** Tooltip / aria-label for the marker and overlay. */
  label: string;
  /** Coordinate for the click ripple overlay (in source pixels). */
  coordinate?: [number, number];
  /** Added to coordinate to account for a scrolled viewport. */
  viewportOffset?: [number, number];
  /** Raw action type (e.g. left_click, type) — drives ripple vs. label. */
  actionType?: string;
  actionText?: string;
}

const PLAYBACK_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;
const PLAYBACK_RATE_STORAGE_KEY = 'sessionVideoPlaybackRate';
// Skip-inactivity: jump forward when the gap to the next action exceeds this,
// landing slightly before the action so its context isn't clipped.
const INACTIVITY_GAP_MS = 3000;
const INACTIVITY_LEAD_MS = 500;

function formatTime(seconds: number): string {
  const safe = Number.isFinite(seconds) ? seconds : 0;
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function readStoredRate(): number {
  if (typeof window === 'undefined') return 1;
  const raw = window.localStorage.getItem(PLAYBACK_RATE_STORAGE_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return PLAYBACK_RATES.some((r) => r === parsed) ? parsed : 1;
}

interface SessionVideoPlayerProps {
  src: string;
  /**
   * Epoch ms of the recording's final frame (aligns video t=duration to the
   * wall clock). Required to place trim windows and action markers.
   */
  recordingEndsAtMs?: number | null;
  /** Absolute trim window (epoch ms). Applied only when recordingEndsAtMs is set. */
  windowStartMs?: number | null;
  windowEndMs?: number | null;
  /** Meaningful actions to render as timeline markers + drive skip-inactivity. */
  actions?: VideoActionMarker[];
  /** Show the click/label overlay on the video for active actions. */
  showActionOverlay?: boolean;
  /**
   * Fired with the id of the action that is "current" at the playback position
   * (the latest action at or before now) — lets a sibling thread follow along.
   */
  onActiveActionChange?: (id: string | null) => void;
  /** When trimmed, show a "X – Y of Z recording" notice below the controls. */
  showWindowNotice?: boolean;
  className?: string;
}

export function SessionVideoPlayer({
  src,
  recordingEndsAtMs,
  windowStartMs,
  windowEndMs,
  actions = [],
  showActionOverlay = false,
  showWindowNotice = false,
  onActiveActionChange,
  className,
}: SessionVideoPlayerProps) {
  const gt = useGT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [playbackRate, setPlaybackRate] = useState<number>(readStoredRate);
  const [skipInactivity, setSkipInactivity] = useState(false);
  const [scale, setScale] = useState<VideoScale | null>(null);

  // Epoch ms of the recording's first frame, derived once the duration loads.
  const recordingStartMs =
    recordingEndsAtMs != null && duration > 0
      ? recordingEndsAtMs - duration * 1000
      : null;

  // Resolve the trim window (seconds from t=0) from absolute timestamps.
  const startOffset =
    recordingStartMs != null && windowStartMs != null
      ? Math.min(
          Math.max(0, (windowStartMs - recordingStartMs) / 1000),
          duration,
        )
      : 0;
  const endOffset =
    recordingStartMs != null && windowEndMs != null
      ? Math.min(
          Math.max((windowEndMs - recordingStartMs) / 1000, startOffset),
          duration,
        )
      : duration;
  const windowDuration = endOffset - startOffset || duration;
  const relativeTime = Math.max(0, currentTime - startOffset);
  // True when the visible window is a strict subset of the recording.
  const isTrimmed =
    recordingStartMs != null &&
    duration > 0 &&
    (startOffset > 0.5 || endOffset < duration - 0.5);

  const updateScale = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return;
    setScale(
      computeContainedScale(
        v.clientWidth,
        v.clientHeight,
        v.videoWidth,
        v.videoHeight,
      ),
    );
  }, []);

  useResizeObserver(videoRef, updateScale);

  function handleLoadedMetadata() {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration);
    v.playbackRate = playbackRate;
    updateScale();
    setReady(true);
  }

  // Seek to the start of the trim window once loaded.
  useEffect(() => {
    if (ready && videoRef.current && startOffset > 0) {
      videoRef.current.currentTime = startOffset;
    }
  }, [ready, startOffset]);

  // Keep the element's rate in sync and persist the choice.
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        PLAYBACK_RATE_STORAGE_KEY,
        String(playbackRate),
      );
    }
  }, [playbackRate]);

  // Actions resolved to seconds from t=0 (only those placeable on the timeline).
  const resolvedActions = useMemo(() => {
    if (recordingStartMs == null) return [];
    return actions
      .map((a) => ({
        ...a,
        offsetSec: (a.timestampMs - recordingStartMs) / 1000,
      }))
      .filter((a) => a.offsetSec >= 0 && a.offsetSec <= duration);
  }, [actions, recordingStartMs, duration]);

  // Markers that fall inside the trim window, with their position in [0,1].
  const visibleMarkers = useMemo(() => {
    if (windowDuration <= 0) return [];
    return resolvedActions
      .map((a) => ({
        ...a,
        windowPosition: (a.offsetSec - startOffset) / windowDuration,
      }))
      .filter((a) => a.windowPosition >= 0 && a.windowPosition <= 1);
  }, [resolvedActions, startOffset, windowDuration]);

  // Sorted action times (seconds from t=0) inside the window for skip logic.
  const actionTimesSec = useMemo(
    () =>
      resolvedActions
        .map((a) => a.offsetSec)
        .filter((t) => t >= startOffset && t <= endOffset)
        .sort((a, b) => a - b),
    [resolvedActions, startOffset, endOffset],
  );

  // The "current" action = the latest one at or before the playback position.
  // WF-77: the Kernel replay was verified to be real-time (idle preserved, playback-seconds ==
  // wall-clock), so this end-anchored mapping (recordingStartMs = recordingEndsAtMs - duration)
  // is exact — PROVIDED recordingEndsAtMs is the recording's true stop time. Callers should pass
  // the persisted stop time (RecordingPlayer prefers it); completedAt precedes the stop and shifts
  // every marker right by that gap.
  const activeActionId = useMemo(() => {
    let bestId: string | null = null;
    let bestOffset = -Infinity;
    for (const a of resolvedActions) {
      if (a.offsetSec <= currentTime && a.offsetSec > bestOffset) {
        bestOffset = a.offsetSec;
        bestId = a.id;
      }
    }
    return bestId;
  }, [resolvedActions, currentTime]);

  useEffect(() => {
    onActiveActionChange?.(activeActionId);
  }, [activeActionId, onActiveActionChange]);

  const activeRipples = useMemo(() => {
    if (!showActionOverlay) return [];
    return resolvedActions.filter(
      (a) =>
        a.coordinate &&
        a.viewportOffset &&
        isClickAction(a.actionType) &&
        currentTime >= a.offsetSec &&
        currentTime <= a.offsetSec + 0.5,
    );
  }, [resolvedActions, currentTime, showActionOverlay]);

  const activeLabel = useMemo(() => {
    if (!showActionOverlay) return null;
    const labels = resolvedActions.filter(
      (a) =>
        a.actionType &&
        !isClickAction(a.actionType) &&
        currentTime >= a.offsetSec &&
        currentTime <= a.offsetSec + 0.5,
    );
    return labels.length > 0 ? labels[labels.length - 1] : null;
  }, [resolvedActions, currentTime, showActionOverlay]);

  function handleTimeUpdate() {
    const v = videoRef.current;
    if (!v) return;

    // Stop at the end of the trim window.
    if (endOffset > 0 && v.currentTime >= endOffset) {
      v.pause();
      v.currentTime = endOffset;
      setPlaying(false);
      setCurrentTime(endOffset);
      return;
    }

    // Skip-inactivity: jump dead air between meaningful actions.
    if (skipInactivity && !v.paused && actionTimesSec.length > 0) {
      const nowMs = v.currentTime * 1000;
      const next = actionTimesSec.find((t) => t * 1000 > nowMs + 1);
      if (next != null) {
        const gapMs = next * 1000 - nowMs;
        if (gapMs > INACTIVITY_GAP_MS) {
          v.currentTime = Math.max(0, next - INACTIVITY_LEAD_MS / 1000);
          setCurrentTime(v.currentTime);
          return;
        }
      }
    }

    setCurrentTime(v.currentTime);
  }

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      if (endOffset > 0 && v.currentTime >= endOffset) {
        v.currentTime = startOffset;
      }
      void v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  }

  function seekToWindowFraction(fraction: number) {
    const v = videoRef.current;
    if (!v) return;
    const clamped = Math.min(1, Math.max(0, fraction));
    v.currentTime = startOffset + clamped * windowDuration;
    setCurrentTime(v.currentTime);
  }

  function seekToSec(offsetSec: number) {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = offsetSec;
    setCurrentTime(v.currentTime);
  }

  const progressFraction =
    windowDuration > 0 ? relativeTime / windowDuration : 0;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-border bg-black',
        className,
      )}
    >
      <div className="relative">
        <video
          ref={videoRef}
          src={src}
          muted={muted}
          className="h-auto max-h-[75vh] w-full"
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onClick={togglePlay}
        />
        {showActionOverlay && (
          <div className="pointer-events-none absolute inset-0">
            {scale &&
              activeRipples.map(
                (a) =>
                  a.coordinate &&
                  a.viewportOffset && (
                    <ClickRipple
                      key={a.id}
                      x={a.coordinate[0] + a.viewportOffset[0]}
                      y={a.coordinate[1] + a.viewportOffset[1]}
                      scale={scale}
                    />
                  ),
              )}
            {activeLabel && (
              <div className="absolute bottom-2 left-0 right-0 bg-black/60 px-2 py-1">
                <span className="text-xs text-white">
                  {formatActionLabel(
                    activeLabel.actionType,
                    activeLabel.actionText,
                  )}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scrubber — explicit light-on-dark colors for contrast */}
      <div className="bg-stone-900 px-3 pt-2.5">
        <div className="relative h-3 w-full">
          {/* Track */}
          <div className="absolute top-1/2 h-1 w-full -translate-y-1/2 rounded-full bg-white/25" />
          {/* Played range */}
          <div
            className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-white"
            style={{ width: `${Math.min(100, progressFraction * 100)}%` }}
          />
          {/* Playhead thumb */}
          <div
            className="pointer-events-none absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow"
            style={{ left: `${Math.min(100, progressFraction * 100)}%` }}
          />
          {/* Invisible seek hit-area */}
          <input
            type="range"
            min={0}
            max={1}
            step={0.001}
            value={progressFraction}
            onChange={(e) => seekToWindowFraction(Number(e.target.value))}
            aria-label={gt('Seek')}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </div>
      </div>

      {/* Interaction timeline — a dedicated lane of clickable action markers */}
      {visibleMarkers.length > 0 && (
        <div className="bg-stone-900 px-3 pt-1.5">
          <div className="relative h-5 w-full overflow-hidden rounded bg-stone-800">
            {/* Playhead */}
            <div
              className="pointer-events-none absolute top-0 z-10 h-full w-0.5 bg-white/70"
              style={{ left: `${Math.min(100, progressFraction * 100)}%` }}
            />
            {visibleMarkers.map((m) => (
              <button
                type="button"
                key={m.id}
                aria-label={m.label}
                title={m.label}
                onClick={() => seekToSec(m.offsetSec)}
                className="absolute top-1 h-3 w-1 -translate-x-1/2 rounded-sm bg-amber-400 transition-colors hover:h-3.5 hover:bg-amber-300"
                style={{ left: `${m.windowPosition * 100}%` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Control row */}
      <div className="flex items-center gap-1.5 bg-stone-900 px-3 pb-2 pt-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-white hover:bg-white/15"
          onClick={togglePlay}
          aria-label={playing ? gt('Pause') : gt('Play')}
        >
          {playing ? (
            <PauseIcon className="size-4" weight="fill" />
          ) : (
            <PlayIcon className="size-4" weight="fill" />
          )}
        </Button>

        <span className="min-w-[78px] text-center text-[11px] tabular-nums text-stone-300">
          {formatTime(relativeTime)} / {formatTime(windowDuration)}
        </span>

        <div className="flex-1" />

        {actionTimesSec.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-7 gap-1 px-2 text-[11px] text-white hover:bg-white/15',
              skipInactivity && 'bg-white/15 text-amber-300',
            )}
            onClick={() => setSkipInactivity((s) => !s)}
            aria-pressed={skipInactivity}
            aria-label={gt('Skip inactivity between actions')}
            title={gt('Skip inactivity between actions')}
          >
            <FastForwardIcon className="size-3.5" weight="fill" />
            <T>Skip idle</T>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-0.5 px-2 text-[11px] tabular-nums text-white hover:bg-white/15"
              aria-label={gt('Playback speed')}
            >
              {playbackRate}×
              <CaretDownIcon className="size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[120px]">
            <DropdownMenuRadioGroup
              value={String(playbackRate)}
              onValueChange={(val) => setPlaybackRate(Number(val))}
            >
              {PLAYBACK_RATES.map((rate) => (
                <DropdownMenuRadioItem key={rate} value={String(rate)}>
                  {rate === 1 ? gt('Normal') : `${rate}×`}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-white hover:bg-white/15"
          onClick={() => setMuted((m) => !m)}
          aria-label={muted ? gt('Unmute') : gt('Mute')}
        >
          {muted ? (
            <MuteIcon className="size-4" />
          ) : (
            <VolumeIcon className="size-4" />
          )}
        </Button>
      </div>

      {/* Trim-window notice — which slice of the full recording is shown */}
      {showWindowNotice && isTrimmed && (
        <div className="bg-stone-900 px-3 pb-2 text-[10px] text-stone-400">
          <T>
            Showing <Var>{formatTime(startOffset)}</Var>–
            <Var>{formatTime(endOffset)}</Var> of the full{' '}
            <Var>{formatTime(duration)}</Var> recording
          </T>
        </div>
      )}
    </div>
  );
}
